import { RELATIONSHIP_TYPES } from '@constants/common/relationShipTypes';
import { TXN_REASON, TXN_REASONS_TEXT, TXN_TYPES } from '@constants/enrollmentList/enrollmentList.constant';
import { TEXTAREA_MAX_LENGTH } from '@constants/enrollments/enrollments.constants';
import { IMember } from '../../../custom-grouping/types/customGroupingTypes';
import { IEnrollee } from './types';

export const changeFieldValue = (setValue, name: string, value: any) => {
    setValue(name, value, {
        shouldDirty: true,
        shouldValidate: true,
    });
};

export const aptcAmountFieldValue = (setValue, name: string, value: any) => {
    setValue(name, value, {
        shouldDirty: true,
    });
};

/**
 * Updates the value of a currency field in the enrollment premium DTO list.
 *
 * @param setValue - The function to set the value of the field.
 * @param field - The field name to be updated.
 * @param premium - An object containing the month information.
 * @param value - The new value to be set, as a string.
 *
 * The function ensures that the value is a valid currency format before updating.
 * It will not update the value if it ends with '.', '.0', or '.00'.
 *
 * @example
 * // Assuming setValue is a function that updates the state,
 * // field is 'premiumAmount', premium is { month: 3 }, and value is '123.45'
 * currencyChange(setValue, 'premiumAmount', { month: 3 }, '123.45');
 * // This will call setValue with the path 'enrollmentPremiumDtoList.2.premiumAmount'
 * // and the value 123.45
 */

export const currencyChange = (setValue, field, premium, value) => {
    const isPartialDecimal = value.endsWith('.') || value.endsWith('.0') || value.endsWith('.00');

    if (isPartialDecimal) {
        // Keep the user's input as-is during typing
        setValue(`enrollmentPremiumDtoList.${premium.month - 1}.${field}`, value || 0, {
            shouldDirty: true,
        });
    } else if (!isNaN(Number(value))) {
        // Convert only when it's a clean, valid number
        setValue(`enrollmentPremiumDtoList.${premium.month - 1}.${field}`, Number(parseFloat(value).toFixed(2)) || 0, {
            shouldDirty: true,
            shouldValidate: true,
        });
    } else {
        // Optional fallback to empty string or 0
        setValue(`enrollmentPremiumDtoList.${premium.month - 1}.${field}`, '', {
            shouldDirty: true,
        });
    }
};

export const setCustomError = (
    fn: (field: string, error: { type: string; message: string }) => void,
    field: string,
    error: string,
    type: string = 'custom',
) => {
    fn(field, {
        type: type,
        message: error,
    });
};

export const getOptionsByTxnType = (txnType: string, relationshipToSubscriber: string) => {
    let options: { text: string; value: string }[] = [];

    if (txnType == TXN_TYPES.Add) {
        options = [...options, { text: TXN_REASONS_TEXT.MEMBER_BENEFIT_SELECTION, value: TXN_REASON.EC }];
    }

    if (txnType == TXN_TYPES.Change) {
        options = [...options, { text: TXN_REASONS_TEXT.BENEFIT_SELECTION, value: TXN_REASON.TwentyNine }];
    }

    if (txnType != TXN_TYPES.Add) {
        options = [...options, { text: TXN_REASONS_TEXT.NO_REASON_GIVEN, value: TXN_REASON.AI }];
    }

    if (
        txnType != TXN_TYPES.Add &&
        txnType != TXN_TYPES.Change &&
        relationshipToSubscriber.toUpperCase() === RELATIONSHIP_TYPES.SELF.value
    ) {
        options = [...options, { text: TXN_REASONS_TEXT.NON_PAYMENT, value: TXN_REASON.FiftyNine }];
    }

    if (txnType != TXN_TYPES.Add && txnType != TXN_TYPES.Change) {
        options = [
            ...options,
            { text: TXN_REASONS_TEXT.VOLUNTARY_WITHDRAWAL, value: TXN_REASON.Fourteen },
            { text: TXN_REASONS_TEXT.DEATH, value: TXN_REASON.ZeroThree },
        ];
    }

    return options;
};

export const validateTextArea = (value: string) => {
    if (value.length > TEXTAREA_MAX_LENGTH) {
        value = value.slice(0, TEXTAREA_MAX_LENGTH);
    }

    return value;
};

export function getDuplicateMemberStrings(duplicateMemberMap: { [key: string]: IMember }): string[] {
    const duplicateMemberStrings: string[] = [];
    for (const key in duplicateMemberMap) {
        if (Object.prototype.hasOwnProperty.call(duplicateMemberMap, key)) {
            const value = duplicateMemberMap[key];
            let memberString = `${key}, ${value.houseHoldCaseId}, ${value.firstName} ${value.lastName}`;
            const enrollmentIds: string[] = [];
            if (value.healthEnrollmentId != null) {
                enrollmentIds.push(value.healthEnrollmentId);
            }
            if (value.dentalEnrollmentId != null) {
                enrollmentIds.push(value.dentalEnrollmentId);
            }
            memberString += enrollmentIds.map(id => `, ${id}`).join('');
            duplicateMemberStrings.push(memberString);
        }
    }
    return duplicateMemberStrings;
}

export const getMemberOrder = relationshipToSubscriber => {
    let order = 4;
    switch (relationshipToSubscriber.toUpperCase()) {
        case RELATIONSHIP_TYPES.SELF.value:
            order = 1;
            break;
        case RELATIONSHIP_TYPES.SPOUSE.value:
            order = 2;
            break;
        case RELATIONSHIP_TYPES.CHILD.value:
            order = 3;
            break;
        default:
            order = 4;
    }

    return order;
};

export const getEnrolleeOrderList = enrolleeDataDtoList => {
    if (!!enrolleeDataDtoList) {
        let enrolleeData = enrolleeDataDtoList?.map(enrollee => ({
            ...enrollee,
            order: getMemberOrder(enrollee?.relationshipToSubscriber),
        }));
        enrolleeData.sort((a, b) => a.order - b.order);

        return enrolleeData.map((enrollee: IEnrollee, index: number) => ({
            ...enrollee,
            index,
        }));
    } else {
        return enrolleeDataDtoList;
    }
};
